import React, { useState, useRef, useEffect, useCallback } from "react";
import { Mic, Send, X, MessageCircle, Square, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = text.trim();
    setInput("");
    setTranscript("");
    setMessages((prev) => [...prev, { role: "user", text: userMsg }]);
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userMsg, session_id: sessionId }),
      });
      const data = await res.json();
      setMessages((prev) => [...prev, { role: "ai", text: data.response }]);
    } catch {
      setMessages((prev) => [...prev, { role: "ai", text: "Errore di connessione. Riprova o scrivimi su WhatsApp: +39 327 383 9782" }]);
    } finally {
      setLoading(false);
    }
  }, [loading, sessionId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const startVoice = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert("Il tuo browser non supporta il riconoscimento vocale. Usa Chrome.");
      return;
    }
    const recognition = new SR();
    recognition.lang = "it-IT";
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.onresult = (e) => {
      const t = Array.from(e.results).map((r) => r[0].transcript).join("");
      setTranscript(t);
      if (e.results[0].isFinal) {
        setRecording(false);
        setTranscript("");
        sendMessage(t);
      }
    };
    recognition.onerror = () => setRecording(false);
    recognition.onend = () => setRecording(false);
    recognitionRef.current = recognition;
    recognition.start();
    setRecording(true);
  }, [sendMessage]);

  const stopVoice = useCallback(() => {
    if (recognitionRef.current) recognitionRef.current.stop();
    setRecording(false);
  }, []);

  const formatAI = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br/>')
      .replace(/• /g, '<span class="text-[#D4AF37]">›</span> ');
  };

  return (
    <>
      {/* Chat Panel */}
      {isOpen && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-24 right-4 z-50 w-[340px] sm:w-[380px] bg-[#0c1220] border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          style={{ maxHeight: "min(520px, 70vh)", animation: "fadeInUp 0.3s ease-out" }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#0c1220] to-[#111827] px-4 py-3 flex items-center justify-between border-b border-white/10 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <div>
                <div className="text-white font-bold text-sm">Riccardo</div>
                <div className="text-[#34A853] text-[10px] font-medium flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#34A853]"></div>
                  Online
                </div>
              </div>
            </div>
            <button
              data-testid="chatbot-close"
              onClick={() => setIsOpen(false)}
              className="text-white/50 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-0">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <div className="text-[#D4AF37] font-black text-2xl leading-tight" style={{ fontFamily: "Poppins, sans-serif" }}>schedabusiness.com</div>
                <p className="text-[#E6E1D2]/50 italic text-xs mt-1 mb-4" style={{ textShadow: "0 0 20px rgba(255,255,255,0.15)", fontFamily: "Poppins, sans-serif" }}>se non ti trovano, non esisti</p>
                <div className="space-y-2">
                  {["Quanto costa un virtual tour?", "Come funziona la formazione?", "Ho un problema con la mia scheda Google"].map((q) => (
                    <button
                      key={q}
                      onClick={() => sendMessage(q)}
                      className="block w-full text-left px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-gray-300 text-xs hover:bg-white/10 hover:border-[#D4AF37]/30 transition-all"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#D4AF37] text-black rounded-br-md font-medium"
                      : "bg-white/5 border border-white/10 text-gray-200 rounded-bl-md"
                  }`}
                  {...(m.role === "ai" ? { dangerouslySetInnerHTML: { __html: formatAI(m.text) } } : {})}
                >
                  {m.role === "user" ? m.text : null}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white/5 border border-white/10 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-[#D4AF37] animate-bounce" style={{ animationDelay: "0ms" }}></div>
                    <div className="w-2 h-2 rounded-full bg-[#D4AF37] animate-bounce" style={{ animationDelay: "150ms" }}></div>
                    <div className="w-2 h-2 rounded-full bg-[#D4AF37] animate-bounce" style={{ animationDelay: "300ms" }}></div>
                  </div>
                </div>
              </div>
            )}
            {transcript && (
              <div className="flex justify-end">
                <div className="max-w-[85%] px-3 py-2 rounded-2xl bg-[#D4AF37]/30 text-[#D4AF37] rounded-br-md text-sm italic">
                  {transcript}...
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="px-3 py-3 border-t border-white/10 flex gap-2 flex-shrink-0">
            <button
              type="button"
              data-testid="chatbot-mic"
              onClick={recording ? stopVoice : startVoice}
              className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                recording ? "bg-[#EA4335] animate-pulse" : "bg-white/10 hover:bg-white/20"
              }`}
            >
              {recording ? <Square className="w-4 h-4 text-white" /> : <Mic className="w-4 h-4 text-white" />}
            </button>
            <input
              ref={inputRef}
              data-testid="chatbot-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={recording ? "Sto ascoltando..." : "Scrivi un messaggio..."}
              disabled={recording}
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-[#D4AF37]/50 disabled:opacity-50"
            />
            <button
              type="submit"
              data-testid="chatbot-send"
              disabled={!input.trim() || loading}
              className="flex-shrink-0 w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center hover:bg-[#d4a518] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="w-4 h-4 text-black animate-spin" /> : <Send className="w-4 h-4 text-black" />}
            </button>
          </form>
        </div>
      )}

      {/* FAB Button */}
      <button
        data-testid="chatbot-fab"
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-4 sm:right-6 z-50 w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110 bg-[#D4AF37] hover:bg-[#d4a518] group"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-black" />
        ) : (
          <MessageCircle className="w-6 h-6 text-black" />
        )}
        {!isOpen && (
          <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-[#34A853] rounded-full border-2 border-[#0c1220] animate-pulse"></div>
        )}
      </button>
    </>
  );
};
